import { Injectable } from '@angular/core';
import { LocaleTokenValue } from '../../state/models/locale-token';
import { LocaleTokenValueEntityService } from '../../state/entity-services/locale-token-value-entity.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root',
})
export class LocaleValuesService {

  constructor(
    private sanitizer: DomSanitizer,
    private localeValuesEntityService: LocaleTokenValueEntityService,
  ) {
  }

  /**
   * Returns the localized string with placeholders (e.g. `{{key}}`) replaced by corresponding values.
   * Can return either raw string or HTML-safe content depending on the `asHtml` flag.
   *
   * @param key - The localization token key used to find the corresponding localized string.
   * @param fields - (Optional) An array of objects with key-value pairs for placeholder substitution.
   * @param asHtml - (Optional) If true, returns a SafeHtml object for use with [innerHTML].
   * @returns Localized string with placeholder values replaced, either as string or SafeHtml.
   */
  getLocaleValue(key: localeValueKey, fields?: Record<string, any>[], asHtml: boolean = true): string | SafeHtml {
    const localeValues: LocaleTokenValue[] = this.localeValuesEntityService.localeTokenValues$.getValue();
    const localeTokenValue = localeValues.find(lv => lv.localeToken.key === key);
    if (!localeTokenValue) return key;

    let { value } = localeTokenValue;

    if (fields?.length) {
      for (const field of fields) {
        for (const [k, v] of Object.entries(field)) {
          const regex = new RegExp(`{{\\s*${k}\\s*}}`, 'g');
          value = value.replace(regex, String(v));
        }
      }
    }

    return asHtml ? this.sanitizer.bypassSecurityTrustHtml(value) : value;
  }
}


export type localeValueKey =
  'patient.onboarding.page.title.page' |
  'patient.onboarding.page.title' |
  'patient.onboarding.page.item.cognition' |
  'patient.onboarding.page.item.schedule' |
  'patient.onboarding.page.item.home.health' |
  'patient.onboarding.page.item.lock' |
  'patient.onboarding.page.item.favorite' |
  'patient.onboarding.page.button.start' |
  'patient.legal.terms.details.page.title.page' |
  'patient.legal.terms.page.title.page' |
  'patient.legal.terms.page.title' |
  'patient.legal.terms.page.subtitle' |
  'patient.legal.terms.page.button.show' |
  'patient.legal.terms.page.button.accept' |
  'patient.form.steps.title.page.age' |
  'patient.form.steps.title.age' |
  'patient.form.steps.form.age.label' |
  'patient.form.steps.form.age.placeholder' |
  'patient.form.steps.title.page.biologicalsex' |
  'patient.form.steps.title.biologicalsex' |
  'patient.form.steps.subtitle.biologicalsex' |
  'patient.form.steps.form.biologicalsex.male' |
  'patient.form.steps.form.biologicalsex.female' |
  'patient.form.steps.confirm.title' |
  'patient.form.steps.confirm.message' |
  'patient.form.steps.confirm.button.confirm' |
  'patient.form.steps.question.why.title' |
  'patient.login.code.title.page' |
  'patient.login.code.form.phonecode.label' |
  'patient.login.code.form.phonecode.placeholder' |
  'patient.login.code.title' |
  'patient.login.code.submit.error.code.500' |
  'patient.login.code.submit.error.code.50008' |
  'patient.login.code.submit.error.code.else' |
  'patient.login.code.challenge.error.closedunexpectedly' |
  'patient.login.code.challenge.error.fail' |
  'patient.login.code.confirm.title' |
  'patient.login.code.confirm.message' |
  'patient.login.code.confirm.button.confirm' |
  'patient.login.code.confirm.button.cancel' |
  'patient.login.code.challenge.choosechannel.error' |
  'patient.login.code.challenge.choosechannel.error.resend' |
  'patient.login.code.code.sent' |
  'patient.login.code.dialog.help.confirm.title' |
  'patient.login.code.dialog.help.confirm.message' |
  'patient.login.code.dialog.help.confirm.button.confirm' |
  'patient.login.code.dialog.help.confirm.button.cancel' |
  'patient.login.code.dialog.help.msg' |
  'patient.login.code.button.resend' |
  'patient.login.signup.form.name.message' |
  'patient.login.signup.form.name.label' |
  'patient.login.signup.form.birthdate.label' |
  'patient.login.signup.form.biologicalsex.label.male' |
  'patient.login.signup.form.biologicalsex.label.female' |
  'patient.login.signup.confirm.button.confirm' |
  'patient.login.signup.confirm.message' |
  'patient.login.signup.confirm.title' |
  'patient.login.signup.question.why.title' |
  'patient.login.signup.subtitle.biologicalsex' |
  'patient.login.signup.title.biologicalsex' |
  'patient.login.signup.title.page.biologicalsex' |
  'patient.login.signup.subtitle.birthday' |
  'patient.login.signup.title.birthday' |
  'patient.login.signup.title.page.birthday' |
  'patient.login.signup.subtitle.name' |
  'patient.login.signup.title.name' |
  'patient.login.signup.title.page.name' |
  'patient.login.phone.confirm.button.cancel' |
  'patient.login.phone.confirm.button.confirm' |
  'patient.login.phone.confirm.message' |
  'patient.login.phone.confirm.title' |
  'patient.login.phone.form.document.placeholder' |
  'patient.login.phone.form.document.label' |
  'patient.login.phone.subtitle' |
  'patient.login.phone.title' |
  'patient.login.phone.title.page' |
  'patient.login.document.whatsapp.msg' |
  'patient.login.document.confirm.button.cancel' |
  'patient.login.document.confirm.button.confirm' |
  'patient.login.document.confirm.message' |
  'patient.login.document.confirm.title' |
  'patient.login.document.form.document.placeholder' |
  'patient.login.document.form.document.label' |
  'patient.login.document.subtitle' |
  'patient.login.document.title' |
  'patient.login.document.title.page' |
  'button.history.back' |
  'button.next' |
  'patient.login.intro.start.button' |
  'patient.login.intro.title.page' |
  'patient.login.intro.step.text' |
  'patient.login.intro.step.label' |
  'patient.login.intro.welcome.subtitle' |
  'patient.login.intro.welcome';
